/* eslint-disable react/jsx-props-no-spreading */
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as Sentry from '@sentry/react';
import { useTranslation } from 'react-i18next';
import useSessionStorage from '../hooks/useSessionStorage';
import { post } from '../services/api';
import Spinner from './Spinner';
import formatMobileNumber from '../utils/mobile';

const formatOrderDataForKustom = (clubData, order, selectedProduct, offersPayment) => {
  const customer = {
    firstname: order.firstname,
    lastname: order.lastname,
    address: order.address,
    post_address: order.postAddress,
    email: order.email,
    mobile: order.mobile
  };

  return {
    ...order,
    ...customer,
    product_id: selectedProduct.id,
    product_code: selectedProduct.code,
    product_name: selectedProduct.name,
    price_customer: selectedProduct.price,
    delivery_fee: addDeliveryFee ? clubData.deliveryFee : 0,
    total_price: totalPrice,
    currency: selectedProduct.currency,
    golfclub_id: clubData.golfclubId,
    callback_url: clubData.parentUrl,
    campaign: clubData.campaign?.code,
    country: clubData.country,
    payment: offersPayment === 0 ? 'club' : offersPayment === 1 ? 'club-online' : 'kustom'
  };
};

let addDeliveryFee = false;
let totalPrice = 0;

function Order({ clubData, callback, paymentCanceled }) {
  const [inProgress, setInProgress] = useState(null);
  const [error, setError] = useState(paymentCanceled && 'Betalningen avbruten');
  const [showAddress, setShowAddress] = useState(false);
  const [product, setProduct] = useState(null);

  const { products, campaign, offersPayment, delivery } = clubData;

  const { t, i18n } = useTranslation();

  const [orderData, setOrderData] = useSessionStorage('order-data', null);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    getValues,
    watch,
    clearErrors,
    trigger
  } = useForm({
    mode: 'onChange',
    // defaultValues: orderData || defaultValues
    defaultValues: orderData || {}
  });

  const productCode = watch('product');
  const number = watch('number');
  // const payment = watch('payment');

  useEffect(() => {
    const prodCode = orderData?.product || products[0]?.code;
    if (prodCode) {
      setValue('product', prodCode);
      onProductChange(prodCode);
    }
  }, [products]);

  useEffect(() => {
    onProductChange();
  }, [productCode]);

  const onProductChange = (prodCode = null) => {
    const tmpProductCode = prodCode || productCode;
    const selProduct = products.find((p) => p.code === tmpProductCode);
    if (!selProduct) return;
    setProduct(selProduct);
    const deliveryType = selProduct?.digital || selProduct.code === 'GHPL' ? 'eml' : orderData?.delivery || '';
    // setValue('product', selProduct);
    setValue('delivery', deliveryType);
    setShowAddress(selProduct && !selProduct?.digital && deliveryType === 'del');
  };

  const onDeliveryChange = ({ target }) => {
    const { value } = target;
    setShowAddress(value === 'del');
    clearErrors(['address', 'postAddress']);
  };

  const onMobileBlur = ({ target }) => {
    const { value: mobile } = target;
    if (mobile) {
      setValue('mobile', formatMobileNumber(mobile, clubData.country));
      trigger('mobile');
    }
  };

  const isFormValid = async () => trigger();

  const placeOrder = async (order = null) => {
    const isValid = await isFormValid();
    if (!isValid) {
      return {};
    }

    order = order || getValues();
    const selectedProduct = products.find((p) => p.code === order.product);

    const postData = formatOrderDataForKustom(clubData, order, selectedProduct, offersPayment);
    Sentry.getCurrentScope().setExtra('orderData', postData);
    const { response, error: err } = await post('', postData, true);
    return { response, error: err };
  };

  const onOrder = async (order) => {
    setError(null);
    setInProgress(true);

    const { response, error: err } = await placeOrder({ ...order, payment: 'kustom' });

    if (err) {
      setInProgress(false);
      setError(err);
      return;
    }

    if (response.kustom?.redirect_url) {
      // Kustom
      setOrderData(order);
      window.top.location.href = response.kustom.redirect_url;
    } else if (response.sale) {
      callback(response.sale);
    }
  };

  const firstError = Object.entries(errors)?.[0]?.[0];
  const currency = product?.currency;
  const isDigitalProduct = product?.digital || product?.code === 'GHPL';
  addDeliveryFee = showAddress && offersPayment === 2;
  totalPrice = ((product?.price || 0) * number || null) + (addDeliveryFee ? clubData.deliveryFee : 0);

  return (
    <div className="content">
      <h2>
        {t('header')} - {clubData.name}
      </h2>
      <p>{clubData.welcomeText || t('welcome')}</p>
      {clubData.paymentText && <p>{clubData.paymentText}</p>}

      {error && <p className="golfamore-errors">{error}</p>}

      {campaign?.name && (
        <div className="campaign">
          <h4>{campaign.name}</h4>
          {/* <p>{campaign.shortDescription}</p> */}
        </div>
      )}

      <form onSubmit={handleSubmit(onOrder)}>
        <div className="row">
          <div className="col-sm-12">
            <label>{t('product')}</label>
            <select
              {...register('product', { required: true })}
              // onChange={(e) => onProductChange({ ...e, prodCode: null })}
              className={errors.number ? 'product' : ''}
            >
              <option value="">{t('productSel')}</option>
              {products?.map((p) => (
                <option key={p.id} value={p.code}>
                  {p.name} ({p.price} {p.currency})
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="row">
          <div className="col-sm-6">
            <label>{t('number')}</label>
            <select
              {...register('number', { required: true })}
              disabled={!product}
              className={errors.number ? 'invalid' : ''}
            >
              <option value="">{t('numberSel')}</option>
              <option value="1">1</option>
              <option value="2">2</option>
              <option value="3">3</option>
              <option value="4">4</option>
              <option value="5">5</option>
              <option value="6">6</option>
              <option value="7">7</option>
              <option value="8">8</option>
              <option value="9">9</option>
              <option value="10">10</option>
            </select>
          </div>

          <div className="col-sm-6">
            <label>{t('delivery')}</label>
            <select
              {...register('delivery', { required: true, onChange: onDeliveryChange })}
              disabled={!product || isDigitalProduct}
              className={errors.delivery ? 'invalid' : ''}
            >
              <option value="">{t('deliverySel')}</option>
              <option value="pu">{t('deliveryOptPu')}</option>
              {delivery === 1 && <option value="del">{t('deliveryOptDel')}</option>}
              <option value="eml" disabled={!isDigitalProduct}>
                {t('deliveryOptEml')}
              </option>
            </select>
          </div>
        </div>

        <div className="row">
          <div className="col-sm-6">
            <label>{t('firstname')}</label>
            <input
              type="text"
              {...register('firstname', { required: true, minLength: 2 })}
              className={errors.firstname ? 'invalid' : ''}
            />
          </div>
          <div className="col-sm-6">
            <label>{t('lastname')}</label>
            <input
              type="text"
              {...register('lastname', { required: true, minLength: 2 })}
              className={errors.lastname ? 'invalid' : ''}
            />
          </div>
        </div>

        {showAddress && (
          <div className="row">
            <div className="col-sm-6">
              <label>{t('address')}</label>
              <input
                type="text"
                {...register('address', { required: true, minLength: 3 })}
                className={errors.address ? 'invalid' : ''}
              />
            </div>
            <div className="col-sm-6">
              <label>
                {t('postAddress')}
                {i18n.exists('postAddressHelp') && <span className="small"> ({t('postAddressHelp')})</span>}
              </label>
              <input
                type="text"
                {...register('postAddress', { required: true, minLength: 6 })}
                className={errors.text ? 'invalid' : ''}
              />
            </div>
          </div>
        )}
        <div className="row">
          <div className="col-sm-6">
            <label>{t('email')}</label>
            <input
              type="email"
              {...register('email', {
                required: true,
                pattern: new RegExp(t('regex.email'))
              })}
              className={errors.email ? 'invalid' : ''}
            />
          </div>

          <div className="col-sm-6">
            <label>{t('mobile')}</label>
            <input
              type="tel"
              {...register('mobile', {
                required: true,
                pattern: new RegExp(t('regex.mobile')),
                onBlur: onMobileBlur
              })}
              className={errors.mobile ? 'invalid' : ''}
            />
            <span className="small">{t('mobileFormat')}</span>
          </div>
        </div>

        {addDeliveryFee && clubData.deliveryFee && <p>{t('deliveryFee', { fee: clubData.deliveryFee, currency })}</p>}

        {offersPayment === 1 && <p style={{ fontWeight: 'bold' }}>{t('paymentSelInfo')}</p>}
        {offersPayment === 0 && <p style={{ fontWeight: 'bold' }}>{t('paymentSelClub')}</p>}

        <div className="row">
          <div className="col-sm-12 mt-2 mb-4">
            <label className={`gdpr-label ${errors.gdpr ? 'invalid' : ''}`}>
              <input
                type="checkbox"
                {...register('gdpr', { required: true })}
                className={errors.gdpr ? 'invalid' : ''}
              />
              <span dangerouslySetInnerHTML={{ __html: t('gdpr') }} />
            </label>
          </div>
        </div>

        {firstError && <p className="golfamore-error">{t(`${firstError}Err`)}</p>}

        <button
          type="submit"
          className="button"
          // disabled={!!firstError || inProgress}
        >
          {inProgress && <Spinner size={12} />}
          {t('order')} {totalPrice > 0 && <>({`${totalPrice}  ${currency}`})</>}
        </button>
      </form>
    </div>
  );
}

export default Order;
